import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { bdsGridModeType } from '@bds/components';

import { CommunicationMessage, CommunicationService } from '@bds/core';
import { FastracDxGridWrapperComponent, FastracScreenBaseComponent } from '@bds/fastrac';
import { CarIdTransformService } from '@bds/helpers';

import { RailtracTripService, RtGpsLocationService } from '@bds/railtrac';

import { RtTrip, RtTripRefFieldDef, RtvGpsLocation } from '@bds/railtrac-models';

import { FastracColsService, SplcCoordinateService } from '@bds/services';

import { faBars } from '@fortawesome/pro-solid-svg-icons';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PageService } from '@bds/core';
import { ErrorListItem } from '@bds/rt-advance-models';
import { Clipboard } from '@angular/cdk/clipboard';
import { BdsErpcOriginService } from '@bds/data-access';

@Component({
    selector: 'app-gps-location-screen',
    templateUrl: './gps-location-screen.component.html',
    styleUrl: './gps-location-screen.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class GpsLocationScreenComponent
    extends FastracScreenBaseComponent
    implements OnInit, OnDestroy
{
    @ViewChild('dxGridWrapper') fastracGridComponent: FastracDxGridWrapperComponent;

    public gridSource: DataSource;
    public allowModeToggle = true;
    public selectedRowKeys: number[] = [];
    public selectedRowData: any[] = [];
    public gridId = 135;
    public gridMode: bdsGridModeType = 'multiple';
    public gridStore: ODataStore;

    gpsList: RtvGpsLocation[] = [];
    selectedRows: RtvGpsLocation[] = [];

    public isCreatingNew = false;
    public newTrip: RtTrip;

    public iconMenu = faBars;

    public tripRefFieldDefs$: Observable<RtTripRefFieldDef[]>;

    isDirty = false;
    errors: ErrorListItem[] = [];

    private ngUnsubscribe$: Subject<boolean> = new Subject<boolean>();

    get grid() {
        return this.fastracGridComponent?.grid;
    }

    constructor(
        public gpsLocationService: RtGpsLocationService,
        public dialog: MatDialog,
        public router: Router,
        public route: ActivatedRoute,
        public pageService: PageService,
        public snackbar: MatSnackBar,
        public gridColumnService: FastracColsService,
        public carIdTransformService: CarIdTransformService,
        public communicationService: CommunicationService,
        public tripService: RailtracTripService,
        public erpcOriginService: BdsErpcOriginService,
        public splcCoordinateService: SplcCoordinateService,
        private clipboard: Clipboard,
    ) {
        super(router, route, gridColumnService, communicationService, carIdTransformService);

        pageService.setHeader('GPS LOCATIONS');

        communicationService
            .getData()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((m) => {
                const message: CommunicationMessage = m as CommunicationMessage;
                if (message && message.subscriberName === 'TripErrorScreen' && message.data) {
                    if (this.grid && this.grid.instance) {
                        void this.grid.instance.refresh();
                    }
                }
            });

        this.gridSource = this.gpsLocationService.getODataStore();
    }

    ngOnInit() {
        super.ngOnInit();
        this.grid?.instance.repaint();
    }

    onSelectionChanged(gpsList: RtvGpsLocation[]): void {
        this.selectedRows = gpsList;
        this.gpsList = gpsList;

        if (gpsList.length === 1) {
            const gps = gpsList[0];
            if (gps.gpsLocationType === 'C') {
                gps.toolTip = gps.carId;
                gps.isMain = true;
                this.gpsList = [gps];

                this.tripService
                    .findCurrentTrip(gps.carInit, gps.carNo)
                    .subscribe((currentTrip) => {
                        if (currentTrip) {
                            this.loadCoordinateForLocation(
                                gps,
                                currentTrip.originCity,
                                currentTrip.originState,
                                'Origin',
                            );
                            this.loadCoordinateForLocation(
                                gps,
                                currentTrip.destinationCity,
                                currentTrip.destinationState,
                                'Destination',
                            );
                        }
                    });
            } else if (gps.gpsLocationType === 'H') {
                this.gpsLocationService
                    .getHistoryGpsViaOdata(gps)
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe((historyGps) => {
                        this.gpsList = historyGps;
                    });
            }
        }
    }

    private loadCoordinateForLocation(
        gps: RtvGpsLocation,
        city: string,
        state: string,
        label: string,
    ): void {
        this.erpcOriginService.getByCityState(city, state).subscribe((location) => {
            if (location) {
                this.splcCoordinateService
                    .getSplcCoordinateBySplc(location.splc)
                    .subscribe((coordinate) => {
                        this.gpsList.push({
                            ...gps,
                            latitude: coordinate?.relatedObject.latitude,
                            longitude: coordinate?.relatedObject.longitude,
                            toolTip: label,
                        });
                    });
            }
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.ngUnsubscribe$.next(true);
        this.ngUnsubscribe$.unsubscribe();
    }

    onDirty(): void {
        this.isDirty = true;
    }

    refreshGrid(): void {
        void this.grid.instance.refresh();
    }

    copyCarsToClipboard(): void {
        this.selectedRowData = this.grid.instance.getSelectedRowsData();
        if (this.selectedRowData != null && this.selectedRowData.length > 0) {
            const carIds = this.selectedRowData.map((eachRow) => {
                return eachRow.carInit.trim() + eachRow.carNo.trim();
            });
            const uniqueCars = [...new Set(carIds)];
            this.clipboard.copy(uniqueCars.join('\n'));
        }
    }

    public onMenuItemClicked(e: unknown): void {
        console.log('onMenuItemClicked', e);
    }

    onGridFastracIdChanged($event: number): void {
        super.onGridFastracIdChanged($event);
        void this.grid.instance.refresh();
    }
}
